import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { IRegistration } from "../../../../interfaces/models/IRegistration";
import type { AppDispatch } from "../../../../redux/store";
import {
  setAdditionalFeeInput,
  setCatalogState,
  setGuests,
  setNewPaymentMethod,
  setPersistedPaymentFields,
  setSelectedSpecialEventIds,
  setShowGuestValidationErrors,
  setSpecialEventGuestsMap,
} from "../../../../redux/states/editParticipant.state";
import {
  derivePaymentStatus,
  normalizeStaffPaymentFields,
} from "../utils/participantPaymentRules";
import {
  hasVisibleGuestWithMissingName,
  normalizeRegistrationGuests,
} from "../../../../utils/registrationGuests";
import {
  hasSpecialEventGuestsWithMissingNames,
  normalizeSpecialEventGuestsMap,
} from "../../../../utils/specialEventGuests";
import {
  selectEditParticipantForm,
  selectEventData,
  selectEventId,
  selectIsEditMode,
  selectRegistrationData,
  selectStaffFormState,
} from "../editParticipant.selectors";
import { useEditParticipantPatch } from "./useEditParticipantPatch";
import { useRegistrantCatalogOptions } from "./useRegistrantCatalogOptions";
import { editParticipantCatalogBridge } from "./editParticipantCatalogBridge";

/** Run once in EditParticipant index. */
export function useEditParticipantFormLoader (): void {
  const dispatch = useDispatch<AppDispatch>();
  const eventId = useSelector(selectEventId);
  const eventData = useSelector(selectEventData);
  const registrationData = useSelector(selectRegistrationData);
  const isEditMode = useSelector(selectIsEditMode);
  const staffFormState = useSelector(selectStaffFormState);
  const form = useSelector(selectEditParticipantForm);
  const { patchRegistration } = useEditParticipantPatch();

  const registrantCatalog = useRegistrantCatalogOptions({
    eventId,
    registrationData,
    isEditMode,
    patchRegistration,
  });

  const catalogSnapshotRef = useRef("");

  useEffect(() => {
    editParticipantCatalogBridge.handlers = {
      handleOrganizationChange: registrantCatalog.handleOrganizationChange,
      handleRegistrantChange: registrantCatalog.handleRegistrantChange,
    };

    const snapshot = JSON.stringify({
      organizationLabel: registrantCatalog.organizationLabel,
      organizationOptions: registrantCatalog.organizationOptions,
      registrantOptions: registrantCatalog.registrantOptions,
    });
    if (catalogSnapshotRef.current === snapshot) {
      return;
    }
    catalogSnapshotRef.current = snapshot;

    dispatch(
      setCatalogState({
        organizationLabel: registrantCatalog.organizationLabel,
        organizationOptions: registrantCatalog.organizationOptions,
        registrantOptions: registrantCatalog.registrantOptions,
      }),
    );
  }, [
    dispatch,
    registrantCatalog.handleOrganizationChange,
    registrantCatalog.handleRegistrantChange,
    registrantCatalog.organizationLabel,
    registrantCatalog.organizationOptions,
    registrantCatalog.registrantOptions,
  ]);

  useEffect(() => {
    if (!registrationData?.id) {
      return;
    }
    dispatch(setGuests(normalizeRegistrationGuests(registrationData)));
    dispatch(
      setSelectedSpecialEventIds(
        Array.isArray(registrationData.selectedSpecialEventIds)
          ? registrationData.selectedSpecialEventIds.filter(Boolean)
          : [],
      ),
    );
    dispatch(
      setSpecialEventGuestsMap(normalizeSpecialEventGuestsMap(registrationData)),
    );
    dispatch(
      setAdditionalFeeInput(
        registrationData.additionalFee != null
          ? String(registrationData.additionalFee)
          : "",
      ),
    );
    dispatch(
      setNewPaymentMethod(""),
    );
    dispatch(
      setPersistedPaymentFields({
        amountPaid: registrationData.amountPaid,
        additionalFee: registrationData.additionalFee,
        paymentMethod: registrationData.paymentMethod,
        paymentStatus: registrationData.paymentStatus,
      }),
    );
    // Seed form once when registration id is loaded (not on every field patch).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- registrationData read at id change only
  }, [dispatch, registrationData?.id]);

  useEffect(() => {
    if (!registrationData) {
      return;
    }

    const normalized = normalizeStaffPaymentFields(
      registrationData,
      eventData,
      staffFormState,
    );
    const patch: Partial<IRegistration> = {};

    if (
      normalized.paymentMethod &&
      normalized.paymentMethod !== registrationData.paymentMethod
    ) {
      patch.paymentMethod = normalized.paymentMethod;
    }

    const draft = { ...registrationData, ...patch };
    const derived = derivePaymentStatus(draft, eventData, staffFormState);
    if (derived !== registrationData.paymentStatus) {
      patch.paymentStatus = derived;
    }

    if (Object.keys(patch).length > 0) {
      patchRegistration(patch);
    }
  }, [
    registrationData?.attendeeType,
    registrationData?.paymentMethod,
    registrationData?.prepaid,
    registrationData?.amountPaid,
    registrationData?.paymentStatus,
    form.guests,
    form.selectedSpecialEventIds,
    form.specialEventGuestsMap,
    form.additionalFeeInput,
    eventData,
    patchRegistration,
    registrationData,
    staffFormState,
  ]);

  useEffect(() => {
    if (!form.showGuestValidationErrors) {
      return;
    }
    if (hasVisibleGuestWithMissingName(form.guests)) {
      return;
    }
    if (
      hasSpecialEventGuestsWithMissingNames(
        form.specialEventGuestsMap,
        form.selectedSpecialEventIds,
      )
    ) {
      return;
    }
    dispatch(setShowGuestValidationErrors(false));
  }, [
    dispatch,
    form.guests,
    form.selectedSpecialEventIds,
    form.showGuestValidationErrors,
    form.specialEventGuestsMap,
  ]);
}
